import React, { useState, useEffect } from 'react';
import { useEmpresa } from '../../contexts/EmpresaContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  Building2, Users, Search, ExternalLink, Eye, 
  AlertTriangle, CheckCircle, Clock, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { useSearchParams, useNavigate } from 'react-router-dom';

const EscritorioEmpresas = () => {
  const { empresasDisponiveis, trocarEmpresa, empresaAtual, loading } = useEmpresa();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [empresaDetalhes, setEmpresaDetalhes] = useState(null);
  const [showDetalhes, setShowDetalhes] = useState(false);
  const [loadingDetalhes, setLoadingDetalhes] = useState(false);
  const [empresasStats, setEmpresasStats] = useState({});

  useEffect(() => {
    // Verificar se há empresa na URL para abrir detalhes
    const empresaId = searchParams.get('empresa');
    if (empresaId && empresasDisponiveis.length > 0) {
      const empresa = empresasDisponiveis.find(e => e.id === empresaId);
      if (empresa) {
        abrirDetalhes(empresa);
      }
    }
  }, [searchParams, empresasDisponiveis]);

  useEffect(() => {
    carregarStatsEmpresas();
  }, [empresasDisponiveis]);

  const carregarStatsEmpresas = async () => {
    if (!empresasDisponiveis || empresasDisponiveis.length === 0) return;

    const stats = {};
    for (const empresa of empresasDisponiveis) {
      try {
        // Buscar empregados
        const empResponse = await api.get(`/empregados?empresa_id=${empresa.id}`);
        const totalEmpregados = empResponse.data?.length || 0;

        // Buscar inconsistências pendentes
        const incResponse = await api.get(`/inconsistencias-ponto?empresa_id=${empresa.id}&status_filtro=pendente`);
        const inconsistencias = incResponse.data?.length || 0;

        stats[empresa.id] = { totalEmpregados, inconsistencias };
      } catch (err) {
        stats[empresa.id] = { totalEmpregados: 0, inconsistencias: 0 };
      }
    }
    setEmpresasStats(stats);
  };

  const abrirDetalhes = async (empresa) => {
    setLoadingDetalhes(true);
    setShowDetalhes(true);
    
    try {
      // Buscar detalhes completos da empresa
      const response = await api.get(`/empresas/${empresa.id}`);
      
      // Buscar estatísticas
      const empResponse = await api.get(`/empregados?empresa_id=${empresa.id}`);
      const incResponse = await api.get(`/inconsistencias-ponto?empresa_id=${empresa.id}&status_filtro=pendente`);
      
      setEmpresaDetalhes({
        ...response.data,
        totalEmpregados: empResponse.data?.length || 0,
        inconsistencias: incResponse.data?.length || 0
      });
    } catch (error) {
      toast.error('Erro ao carregar detalhes da empresa');
      console.error(error);
    } finally {
      setLoadingDetalhes(false);
    }
  };

  const acessarEmpresa = async (empresa) => {
    try {
      const sucesso = await trocarEmpresa(empresa.id);
      if (sucesso) {
        toast.success(`Contexto alterado para ${empresa.nome_fantasia || empresa.razao_social}`);
        // Navegar para a área da empresa
        navigate('/empresa');
      }
    } catch (error) {
      toast.error('Erro ao trocar de empresa');
    }
  };

  const formatCNPJ = (cnpj) => {
    if (!cnpj) return '-';
    const numeros = cnpj.replace(/\D/g, '');
    return numeros.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  const empresasFiltradas = empresasDisponiveis.filter(empresa => {
    const termo = searchTerm.toLowerCase();
    return (
      empresa.razao_social?.toLowerCase().includes(termo) ||
      empresa.nome_fantasia?.toLowerCase().includes(termo) ||
      empresa.cnpj?.includes(termo)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="escritorio-empresas">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Empresas</h1>
          <p className="text-slate-600 mt-1">
            Gerencie as empresas vinculadas ao seu escritório
          </p>
        </div>
        <Badge variant="outline" className="w-fit">
          {empresasDisponiveis.length} empresa{empresasDisponiveis.length !== 1 ? 's' : ''}
        </Badge>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar por nome, razão social ou CNPJ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-empresas"
            />
          </div>
        </CardContent>
      </Card>

      {/* Lista de Empresas */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Empresas</CardTitle>
          <CardDescription>
            Clique em uma empresa para ver detalhes ou acessar o sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          {empresasFiltradas.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <Building2 className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">Nenhuma empresa encontrada</p>
              <p className="text-sm">Ajuste os filtros ou verifique os vínculos</p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empresa</TableHead>
                    <TableHead className="hidden md:table-cell">CNPJ</TableHead>
                    <TableHead className="text-center">Empregados</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {empresasFiltradas.map((empresa) => {
                    const stats = empresasStats[empresa.id] || { totalEmpregados: 0, inconsistencias: 0 };
                    return (
                      <TableRow key={empresa.id} className="cursor-pointer hover:bg-slate-50">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Building2 className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">
                                {empresa.nome_fantasia || empresa.razao_social}
                              </p>
                              {empresa.nome_fantasia && empresa.razao_social !== empresa.nome_fantasia && (
                                <p className="text-xs text-slate-500 truncate max-w-[200px]">
                                  {empresa.razao_social}
                                </p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-slate-600">
                          {formatCNPJ(empresa.cnpj)}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Users className="w-4 h-4 text-slate-400" />
                            <span>{stats.totalEmpregados}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {stats.inconsistencias > 0 ? (
                            <Badge variant="destructive" className="text-xs">
                              {stats.inconsistencias} pendência{stats.inconsistencias !== 1 ? 's' : ''}
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              OK
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => abrirDetalhes(empresa)}
                              data-testid={`btn-detalhes-${empresa.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => acessarEmpresa(empresa)}
                              data-testid={`btn-acessar-${empresa.id}`}
                            >
                              <ExternalLink className="w-4 h-4 mr-1" />
                              Acessar
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Detalhes */}
      <Dialog open={showDetalhes} onOpenChange={setShowDetalhes}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Detalhes da Empresa
            </DialogTitle>
            <DialogDescription>
              Informações completas e estatísticas
            </DialogDescription>
          </DialogHeader>
          
          {loadingDetalhes ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : empresaDetalhes && (
            <div className="space-y-6">
              {/* Dados básicos */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Razão Social</p>
                  <p className="font-medium">{empresaDetalhes.razao_social}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Nome Fantasia</p>
                  <p className="font-medium">{empresaDetalhes.nome_fantasia || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">CNPJ</p>
                  <p className="font-medium">{formatCNPJ(empresaDetalhes.cnpj)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <Badge variant={empresaDetalhes.ativo ? 'default' : 'secondary'}>
                    {empresaDetalhes.ativo ? 'Ativo' : 'Inativo'}
                  </Badge>
                </div>
              </div>

              {/* Estatísticas */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <Users className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                    <p className="text-2xl font-bold">{empresaDetalhes.totalEmpregados}</p>
                    <p className="text-xs text-slate-500">Empregados</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <AlertTriangle className={`w-6 h-6 mx-auto mb-2 ${
                      empresaDetalhes.inconsistencias > 0 ? 'text-red-600' : 'text-green-600'
                    }`} />
                    <p className="text-2xl font-bold">{empresaDetalhes.inconsistencias}</p>
                    <p className="text-xs text-slate-500">Inconsistências</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <Clock className="w-6 h-6 mx-auto mb-2 text-purple-600" />
                    <p className="text-2xl font-bold">
                      {empresaDetalhes.tipo_gestao === 'escritorio' ? 'Escritório' : 'Própria'}
                    </p>
                    <p className="text-xs text-slate-500">Gestão</p>
                  </CardContent>
                </Card>
              </div>

              {/* Endereço */}
              {empresaDetalhes.endereco && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Endereço</p>
                  <p className="font-medium">
                    {empresaDetalhes.endereco.logradouro}, {empresaDetalhes.endereco.numero}
                    {empresaDetalhes.endereco.complemento && ` - ${empresaDetalhes.endereco.complemento}`}
                    <br />
                    {empresaDetalhes.endereco.bairro} - {empresaDetalhes.endereco.cidade}/{empresaDetalhes.endereco.uf}
                    <br />
                    CEP: {empresaDetalhes.endereco.cep}
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetalhes(false)}>
              Fechar
            </Button>
            {empresaDetalhes && (
              <Button onClick={() => acessarEmpresa(empresaDetalhes)}>
                <ExternalLink className="w-4 h-4 mr-2" />
                Acessar Sistema
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EscritorioEmpresas;
